/**
 * 延伸內容視窗「點內容區展開隱藏答案 / 解析」的委派 handler（NBK-1663）。
 *
 * 背景：這個功能原本由書本 layout script 提供（例如
 * public/script/topic/topic108-2.js:38-50），用 addEventListener 直接綁在
 * #answer / #analyze 節點上。但那些節點在綁定之後會被重建——pagedHtml 重設會
 * 換掉 contentRef 的 innerHTML、ExtendedContentContainer 的
 * <MathJax.Text key={element}> 會 remount、MathJax typeset 也會改寫 DOM——
 * listener 隨舊節點一起消失，而 script 只執行一次、沒有重綁機制，
 * 結果就是「點答案區沒反應，只能用右下角按鈕」。
 *
 * 因此改成把 listener 掛在穩定的祖先（contentRef）上做事件委派：祖先不會被
 * innerHTML 替換掉，內容重建幾次都仍然有效。呼叫端必須同時把 layout script
 * 綁在那些節點上的 listener 剝掉（clone-replace），否則同一次點擊會被切兩次
 * 而等於沒切。SynopsisWindow/index.js:59-101 是同一套做法。
 *
 * 各 case 的 `if (!el.onclick)` 守衛：書本 HTML 或 ExtendedContentContainer
 * 內的 setTimeout 可能已掛上 inline onclick，有的話就交給它處理，避免重複切換。
 *
 * TODO：SynopsisWindow 目前仍有一份自己的同款清單，待驗證穩定後改為共用這裡。
 *
 * @param {Event} e - 原生 click event
 * @returns {boolean} 是否命中並處理（false 表示呼叫端可繼續原本的 fallback）
 */
export const handleAnswerToggleClick = (e) => {
  const target = e && e.target;
  if (!target || !target.closest) return false;

  const toggle = (el) => {
    if (el && el.classList) el.classList.toggle('show');
  };

  const wordAns = target.closest('.wordAns');
  if (wordAns) {
    if (!wordAns.onclick) toggle(wordAns);
    return true;
  }

  const check = target.closest('.check');
  if (check) {
    if (!check.onclick) {
      toggle(check.nextSibling && check.nextSibling.nextSibling);
    }
    return true;
  }

  const ansCheck = target.closest('.ansCheck');
  if (ansCheck) {
    if (!ansCheck.onclick) {
      const p = ansCheck.parentNode;
      toggle(p && p.nextSibling && p.nextSibling.nextSibling);
    }
    return true;
  }

  const answers = target.closest('.ANSWERS');
  if (answers) {
    if (!answers.onclick) toggle(answers);
    return true;
  }

  const answer = target.closest('#answer');
  if (answer) {
    if (!answer.onclick) toggle(answer);
    return true;
  }

  const analyze = target.closest('#analyze');
  if (analyze) {
    if (!analyze.onclick) toggle(analyze);
    return true;
  }

  // 注意順序：#answer / #analyze 內也有 .ans，故 .ans 必須排在它們之後，
  // 並額外限定在 .fill 題型內，避免誤判。
  const fillAns = target.closest('.ans');
  if (fillAns && fillAns.closest('.fill')) {
    if (!fillAns.onclick) toggle(fillAns);
    return true;
  }

  return false;
};

/**
 * 剝掉 layout script 綁在答案/解析節點上的 toggle listener。
 *
 * cloneNode 不會複製 addEventListener 註冊的 listener，所以 clone-replace
 * 之後那些節點就只剩委派 handler 一個 owner，不會出現「切兩次等於沒切」。
 * 必須在 script 載入完成後才執行，否則它之後才綁上去。
 *
 * 只剝答案/解析這幾個節點，#question 內 .option 的答對錯判斷（同一支 script
 * 的前半段，用 el.onclick 綁在 .option 上）不受影響。
 *
 * 只淺複製節點本身，子節點原封不動搬進新節點，不可整棵深複製（NBK-1701）：
 * layout script 的 listener 只綁在 #answer / #analyze 節點本身，換掉它就夠了。
 * 深複製會連帶複製 MathJax 已排版完成的公式——複製出來的 <script type="math/mml">
 * 不帶 MathJax 記錄狀態的 JS 屬性，下一次排版（例如點畫筆讓容器 re-render，
 * <MathJax.Text> 重新 typeset）會把它當成新公式再排一次，新輸出插在舊輸出與
 * <script> 之間。postTranslate 只拿掉 script 前一個節點的 class，舊輸出就永遠
 * 卡在 .MJXc-processed（display: none）；強制顯示它又會變成重複的兩份。
 * 版型 script 載入（非同步）若晚於 MathJax 排版完成就會發生，所以時好時壞。
 * 搬移的是同一批節點，MathJax 的狀態都還在，之後重新排版也會認得已處理過。
 *
 * @param {HTMLElement|Document} root - 內容容器（scope 限定在此，不影響其他視窗）
 */
export const stripLayoutScriptAnswerListeners = (root) => {
  if (!root || !root.querySelector) return;

  ['#question ~ #answer', '#question #answer', '#question ~ #analyze'].forEach(
    (selector) => {
      const el = root.querySelector(selector);
      if (el && el.parentNode) {
        const bare = el.cloneNode(false);
        while (el.firstChild) bare.appendChild(el.firstChild);
        el.parentNode.replaceChild(bare, el);
      }
    },
  );
};
